import React, { useState, useEffect } from 'react';
import {
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Typography,
   Button,
   TextField,
   Box,
   Grid,
   CircularProgress,
   Paper,
   Chip,
   List,
   ListItem,
   ListItemAvatar,
   Avatar,
   ListItemText,
   FormControl,
   InputLabel,
   Select,
   MenuItem,
   Tabs,
   Tab,
   Alert,
   Accordion,
   AccordionSummary,
   AccordionDetails,
   FormHelperText,
   ListSubheader,
   SelectChangeEvent,
   Autocomplete,
   FormControlLabel,
   Switch,
} from '@mui/material';
import {
   Email as EmailIcon,
   Phone as PhoneIcon,
   Send as SendIcon,
   PersonAdd as PersonAddIcon,
   Chat as ChatIcon,
   Comment as CommentIcon,
   Image as ImageIcon,
   Edit as EditIcon,
   People as PeopleIcon,
   ExpandMore as ExpandMoreIcon,
   ContactMail as ContactMailIcon,
   AdminPanelSettings as CoOwnerIcon,
   Person as PersonIcon,
} from '@mui/icons-material';
import { useUser } from '../../contexts/UserContext';
import {
   collection,
   query,
   where,
   getDocs,
   orderBy,
   addDoc,
   serverTimestamp,
   Timestamp,
   doc,
   getDoc,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { formatPhoneNumber, isValidPhoneNumber } from '../../utils/smsUtils';
import { sendTripInvitation } from '../../services/tripInvitationService';
import { sendEventInvitation } from '../../services/eventInvitationService';
import {
   RelationshipType,
   relationshipCategories,
   relationshipLabels,
} from '../../types/Relationship';
import { TripRole } from '../../types/Trip';
import { EventRole } from '../../types/Event';
import { getAuthHeaders } from '../../utils/auth';
import axios from 'axios';
import { Country, COUNTRIES } from '../../data/countries';

interface Contact {
   name: string;
   email: string;
}

interface Trip {
   id: string;
   title: string;
   startDate: Timestamp;
   endDate: Timestamp;
   createdBy: string;
   location?: string;
   members?: Array<{
      userId: string;
      email: string;
      displayName?: string;
      photoURL?: string;
      role: 'owner' | 'editor' | 'viewer';
      joinedAt?: any;
   }>;
}

interface Event {
   id: string;
   title: string;
   start: Timestamp;
   end: Timestamp;
   createdBy: string;
   location?: string;
   members?: Array<{
      userId: string;
      email: string;
      displayName?: string;
      photoURL?: string;
      role: 'owner' | 'editor' | 'viewer';
      joinedAt?: any;
   }>;
}

// Update the TripInvitation interface to include relationship and role
interface TripInvitation {
   tripId: string;
   tripTitle: string;
   inviterId: string;
   inviterName: string;
   email: string;
   role: 'viewer' | 'editor' | 'owner';
   customMessage?: string;
   expiresAt: Date;
   relationship?: {
      type: RelationshipType;
      label?: string;
      notes?: string;
   };
}

interface InviteeData {
   name?: string;
   email?: string;
   phoneNumber?: string;
   formattedNumber?: string;
   role?: 'viewer' | 'editor' | 'owner';
   relationship?: {
      type: RelationshipType;
      label?: string;
      notes?: string;
   };
}

interface SendInvitationDialogProps {
   open: boolean;
   onClose: () => void;
   initialTripId?: string;
   initialEventId?: string;
   onInvitationSent?: () => void;
}

const SendInvitationDialog: React.FC<SendInvitationDialogProps> = ({
   open,
   onClose,
   initialTripId,
   initialEventId,
   onInvitationSent,
}) => {
   const { currentUser } = useUser();

   // State for trips
   const [trips, setTrips] = useState<Trip[]>([]);
   const [tripsLoading, setTripsLoading] = useState(true);
   const [selectedTripId, setSelectedTripId] = useState<string>(initialTripId || '');

   // State for events
   const [events, setEvents] = useState<Event[]>([]);
   const [eventsLoading, setEventsLoading] = useState(false);
   const [selectedEventId, setSelectedEventId] = useState<string>(initialEventId || '');

   // Participants
   const [participants, setParticipants] = useState<any[]>([]);
   const [participantsLoading, setParticipantsLoading] = useState(false);

   // Recipients
   const [emailRecipients, setEmailRecipients] = useState<InviteeData[]>([]);
   const [emailInput, setEmailInput] = useState('');
   const [phoneRecipients, setPhoneRecipients] = useState<InviteeData[]>([]);
   const [phoneInput, setPhoneInput] = useState('');
   const [selectedCountry, setSelectedCountry] = useState<Country>(COUNTRIES[0]); // Default to US
   const [customMessage, setCustomMessage] = useState('');

   // Contact selection states
   const [contacts, setContacts] = useState<Contact[]>([]);
   const [loadingContacts, setLoadingContacts] = useState(false);
   const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
   const [useExistingContact, setUseExistingContact] = useState(false);

   // UI State
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [success, setSuccess] = useState<string | null>(null);
   const [tabValue, setTabValue] = useState(0);
   const [recipientsName, setRecipientsName] = useState('');

   // Relationship state
   const [showRelationshipFields, setShowRelationshipFields] = useState(false);
   const [selectedRelationshipType, setSelectedRelationshipType] = useState<RelationshipType | ''>(
      ''
   );
   const [customRelationshipLabel, setCustomRelationshipLabel] = useState('');

   // Role selection state
   const [selectedRole, setSelectedRole] = useState<'viewer' | 'editor' | 'owner'>('viewer');

   // Check if current user has permission to send invites (owner or editor only)
   const canSendInvites = React.useMemo(() => {
      if (!currentUser) {
         console.log('🔍 SendInvite: No currentUser');
         return false;
      }

      // Check for selected trip
      if (selectedTripId) {
         const selectedTrip = trips.find((trip) => trip.id === selectedTripId);
         console.log('🔍 SendInvite: Checking trip permissions:', {
            selectedTripId,
            selectedTrip: selectedTrip
               ? {
                    id: selectedTrip.id,
                    title: selectedTrip.title,
                    members: selectedTrip.members,
                 }
               : null,
            currentUserId: currentUser.uid,
            currentUserEmail: currentUser.email,
         });

         if (selectedTrip) {
            // Check user role in members array (role-based access control only)
            if (selectedTrip.members && Array.isArray(selectedTrip.members)) {
               const userMember = selectedTrip.members.find(
                  (member) =>
                     member.userId === currentUser.uid || member.email === currentUser.email
               );
               console.log('🔍 SendInvite: Found trip member:', userMember);
               const hasPermission =
                  userMember && (userMember.role === 'owner' || userMember.role === 'editor');
               console.log('🔍 SendInvite: Trip permission:', hasPermission);
               return hasPermission;
            }
         }
         console.log('❌ SendInvite: No trip permission');
         return false;
      }

      // Check for selected event
      if (selectedEventId) {
         const selectedEvent = events.find((event) => event.id === selectedEventId);
         console.log('🔍 SendInvite: Checking event permissions:', {
            selectedEventId,
            selectedEvent: selectedEvent
               ? {
                    id: selectedEvent.id,
                    title: selectedEvent.title,
                    members: selectedEvent.members,
                 }
               : null,
            currentUserId: currentUser.uid,
            currentUserEmail: currentUser.email,
         });

         if (selectedEvent) {
            // Check user role in members array (role-based access control only)
            if (selectedEvent.members && Array.isArray(selectedEvent.members)) {
               const userMember = selectedEvent.members.find(
                  (member) =>
                     member.userId === currentUser.uid || member.email === currentUser.email
               );
               console.log('🔍 SendInvite: Found event member:', userMember);
               const hasPermission =
                  userMember && (userMember.role === 'owner' || userMember.role === 'editor');
               console.log('🔍 SendInvite: Event permission:', hasPermission);
               return hasPermission;
            }
         }
         console.log('❌ SendInvite: No event permission');
         return false;
      }

      // If no trip or event selected, allow (they can select one they have permission for)
      console.log('✅ SendInvite: No selection, allowing');
      return true;
   }, [currentUser, selectedTripId, selectedEventId, trips, events]);

   // Debug button state
   React.useEffect(() => {
      console.log('🔍 SendInvite: Button state debug:', {
         canSendInvites,
         loading,
         selectedTripId,
         selectedEventId,
         emailRecipientsLength: emailRecipients.length,
         buttonShouldBeEnabled:
            !loading &&
            (selectedTripId || selectedEventId) &&
            emailRecipients.length > 0 &&
            canSendInvites,
      });
   }, [canSendInvites, loading, selectedTripId, selectedEventId, emailRecipients.length]);

   // Load trips and events when dialog opens
   useEffect(() => {
      if (open && currentUser) {
         fetchTrips();
         fetchEvents();
         fetchContacts();
      }
   }, [open, currentUser]);

   const fetchContacts = async () => {
      if (!currentUser) return;

      setLoadingContacts(true);
      try {
         const headers = await getAuthHeaders();
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/people/contacts?userId=${currentUser.uid}`,
            {
               headers,
            }
         );

         if (response.ok) {
            const data = await response.json();
            console.log('Contacts fetched from relationship_network:', data);

            // Transform the relationship_network data to match Contact interface
            const contactsList = (data.data || []).map((contact: any) => ({
               name: contact.displayName || contact.email,
               email: contact.email,
            }));

            setContacts(contactsList);
         }
      } catch (err) {
         console.error('Error fetching contacts:', err);
         // Don't show error to user, just fail silently for contacts
      } finally {
         setLoadingContacts(false);
      }
   };

   // Fetch participants when a trip is selected
   useEffect(() => {
      if (selectedTripId) {
         fetchParticipants(selectedTripId);
      } else {
         setParticipants([]);
      }
   }, [selectedTripId]);

   // Reset the dialog when it closes
   useEffect(() => {
      if (!open) {
         resetForm();
      }
   }, [open]);

   // Update initial values based on props
   useEffect(() => {
      if (open) {
         if (initialTripId) {
            setSelectedTripId(initialTripId);
         }
         if (initialEventId) {
            setSelectedEventId(initialEventId);
         }
      }
   }, [open, initialTripId, initialEventId]);

   // Reset error message when selection changes
   useEffect(() => {
      setError(null);
   }, [selectedTripId, selectedEventId]);

   const fetchTrips = async () => {
      if (!currentUser) return;

      setTripsLoading(true);
      try {
         console.log('🔍 SendInvite: Fetching trips for user:', currentUser.uid);

         // Get all trips where user is in members array or is creator
         const tripsQuery = query(collection(db, 'trips'));
         const tripsSnapshot = await getDocs(tripsQuery);

         const tripsData: Trip[] = [];

         tripsSnapshot.forEach((doc) => {
            const data = doc.data();
            const members = data.members || [];

            // Check if current user is in members array or is creator
            const isCreator = data.createdBy === currentUser.uid;
            const isMember = members.some(
               (member: any) =>
                  member.userId === currentUser.uid || member.email === currentUser.email
            );

            if (isCreator || isMember) {
               tripsData.push({
                  id: doc.id,
                  title: data.title,
                  startDate: data.startDate,
                  endDate: data.endDate,
                  createdBy: data.createdBy,
                  location: data.primaryDestination,
                  members: members, // Include the full members array
               });
            }
         });

         console.log('🔍 SendInvite: Fetched trips:', tripsData.length, tripsData);
         setTrips(tripsData);
      } catch (error) {
         console.error('❌ SendInvite: Error fetching trips:', error);
         setError('Failed to load trips');
      } finally {
         setTripsLoading(false);
      }
   };

   const fetchEvents = async () => {
      if (!currentUser) return;

      setEventsLoading(true);
      try {
         console.log('🔍 SendInvite: Fetching events for user:', currentUser.uid);

         // Get all events where user is in members array or is creator
         const eventsQuery = query(collection(db, 'events'));
         const eventsSnapshot = await getDocs(eventsQuery);

         const eventsData: Event[] = [];

         eventsSnapshot.forEach((doc) => {
            const data = doc.data();
            const members = data.members || [];

            // Check if current user is in members array or is creator
            const isCreator = data.createdBy === currentUser.uid;
            const isMember = members.some(
               (member: any) =>
                  member.userId === currentUser.uid || member.email === currentUser.email
            );

            if (isCreator || isMember) {
               eventsData.push({
                  id: doc.id,
                  title: data.title,
                  start: data.startDate || data.start,
                  end: data.endDate || data.end,
                  createdBy: data.createdBy,
                  location: data.location,
                  members: members, // Include the full members array
               });
            }
         });

         console.log('🔍 SendInvite: Fetched events:', eventsData.length, eventsData);
         setEvents(eventsData);
      } catch (error) {
         console.error('❌ SendInvite: Error fetching events:', error);
         setError('Failed to load events');
      } finally {
         setEventsLoading(false);
      }
   };

   const fetchParticipants = async (tripId: string) => {
      setParticipantsLoading(true);
      try {
         // First get all trip participants
         const q = query(collection(db, 'tripParticipants'), where('tripId', '==', tripId));

         const participantsSnapshot = await getDocs(q);
         const participantsData: any[] = [];

         for (const docSnapshot of participantsSnapshot.docs) {
            const data = docSnapshot.data();

            // Add to participants list, unless it's already there
            if (!participantsData.some((p) => p.id === docSnapshot.id)) {
               participantsData.push({
                  id: docSnapshot.id,
                  ...data,
               });
            }
         }

         // Then get pending invitations
         const pendingQuery = query(
            collection(db, 'tripInvitations'),
            where('tripId', '==', tripId),
            where('status', '==', 'pending')
         );

         const pendingSnapshot = await getDocs(pendingQuery);

         pendingSnapshot.forEach((doc) => {
            const data = doc.data();

            // Check if this person is already in the participants list
            if (
               !participantsData.some(
                  (p) =>
                     (p.email && p.email === data.email) ||
                     (p.phoneNumber && p.phoneNumber === data.phoneNumber)
               )
            ) {
               participantsData.push({
                  id: doc.id,
                  ...data,
                  pending: true,
               });
            }
         });

         setParticipants(participantsData);
      } catch (error) {
         console.error('Error fetching participants:', error);
         setError('Failed to load participants');
      } finally {
         setParticipantsLoading(false);
      }
   };

   const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
      setTabValue(newValue);
   };

   const handleRelationshipTypeChange = (event: SelectChangeEvent<string>) => {
      setSelectedRelationshipType(event.target.value as RelationshipType);
   };

   const handleAddEmail = () => {
      // Determine which email to use
      const emailToUse = useExistingContact ? selectedContact?.email : emailInput.trim();

      if (!recipientsName) {
         setError('Please enter a name');
         return;
      }

      if (!emailToUse) {
         setError('Please enter an email address or select a contact');
         return;
      }

      const email = emailToUse.toLowerCase();

      // Check if the email is valid
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
         setError('Please enter a valid email address');
         return;
      }

      // Check if the email is already in the list
      if (emailRecipients.some((r) => r.email === email)) {
         setError('This email is already in the recipient list');
         return;
      }

      // Create new recipient with relationship data if available
      const newRecipient: InviteeData = {
         email,
         name: recipientsName,
         role: selectedRole,
      };

      if (selectedRelationshipType) {
         newRecipient.relationship = {
            type: selectedRelationshipType as RelationshipType,
            label: customRelationshipLabel || undefined,
         };
      }

      setEmailRecipients([...emailRecipients, newRecipient]);
      setEmailInput('');
      setSelectedContact(null);
      setUseExistingContact(false);
      setError(null);
      setRecipientsName('');

      // Reset relationship fields after adding
      resetRelationshipFields();
   };

   const handleRemoveEmail = (email: string) => {
      setEmailRecipients(emailRecipients.filter((r) => r.email !== email));
   };

   const handleAddPhone = () => {
      let phone = phoneInput.trim();

      if (!recipientsName.trim()) {
         setError('Please enter a recipient name');
         return;
      }

      if (!phone) {
         setError('Please enter a phone number');
         return;
      }

      // Auto-add country code if not present
      if (!phone.startsWith('+')) {
         phone = selectedCountry.dialCode + phone;
      }

      try {
         // Use our existing utility functions instead of PhoneNumberUtil
         if (!isValidPhoneNumber(phone)) {
            setError('Please enter a valid phone number');
            return;
         }

         const formattedNumber = formatPhoneNumber(phone);

         // Check if the phone is already in the list
         if (phoneRecipients.some((r) => r.phoneNumber === phone)) {
            setError('This phone number is already in the recipient list');
            return;
         }

         // Create new recipient with relationship data if available
         const newRecipient: InviteeData = {
            name: recipientsName.trim(),
            phoneNumber: phone,
            formattedNumber,
            role: selectedRole,
         };

         if (selectedRelationshipType) {
            newRecipient.relationship = {
               type: selectedRelationshipType as RelationshipType,
               label: customRelationshipLabel || undefined,
            };
         }

         setPhoneRecipients([...phoneRecipients, newRecipient]);
         setPhoneInput('');
         setRecipientsName('');
         setError(null);

         // Reset relationship fields after adding
         resetRelationshipFields();
      } catch (error) {
         console.error('Error parsing phone number:', error);
         setError('Please enter a valid phone number in the format: +1 555-123-4567');
      }
   };

   const handleRemovePhone = (formattedNumber: string) => {
      setPhoneRecipients(phoneRecipients.filter((r) => r.formattedNumber !== formattedNumber));
   };

   const resetRelationshipFields = () => {
      setSelectedRelationshipType('');
      setCustomRelationshipLabel('');
      setShowRelationshipFields(false);
   };

   const resetForm = () => {
      setSelectedTripId('');
      setSelectedEventId('');
      setEmailRecipients([]);
      setEmailInput('');
      setPhoneRecipients([]);
      setPhoneInput('');
      setSelectedCountry(COUNTRIES[0]); // Reset to default country (US)
      setCustomMessage('');
      setError(null);
      setSuccess(null);
      setTabValue(0);
      resetRelationshipFields();
      setRecipientsName('');
   };

   const handleDialogClose = () => {
      resetForm();
      onClose();
   };

   const handleSendInvitations = async () => {
      if (!currentUser) {
         setError('You need to be logged in to send invitations');
         return;
      }

      if (tabValue === 0 && emailRecipients.length === 0) {
         setError('Please add at least one email recipient');
         return;
      }

      if (tabValue === 1 && phoneRecipients.length === 0) {
         setError('Please add at least one phone recipient');
         return;
      }

      if (!selectedTripId && !selectedEventId) {
         setError('Please select a trip or event to invite people to');
         return;
      }

      setLoading(true);
      setError(null);
      setSuccess(null);

      // Results tracking
      const results = {
         emailSuccess: 0,
         emailFailed: 0,
         emailDuplicate: 0,
         phoneSuccess: 0,
         phoneFailed: 0,
         duplicateEmails: [] as string[],
         failedEmails: [] as string[],
      };

      try {
         // Find trip data if a trip is selected
         let tripTitle = '';
         if (selectedTripId) {
            const trip = trips.find((t) => t.id === selectedTripId);
            if (trip) {
               tripTitle = trip.title;
            }
         }

         // Find event data if an event is selected
         let eventTitle = '';
         if (selectedEventId) {
            const event = events.find((e) => e.id === selectedEventId);
            if (event) {
               eventTitle = event.title;
            }
         }

         if (tabValue === 0) {
            // For email invitations
            if (selectedTripId && tripTitle) {
               for (const recipient of emailRecipients) {
                  try {
                     // Create invitation data
                     const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

                     const invitation = {
                        tripId: selectedTripId,
                        tripTitle,
                        inviterId: currentUser.uid,
                        inviterName:
                           currentUser.displayName || currentUser.email || 'Trip Organizer',
                        recipient_email: recipient.email!,
                        role: (recipient.role || 'editor') as TripRole,
                        customMessage: customMessage || undefined,
                        expiresAt, // 7 days from now
                        relationship: sanitizeRelationship(recipient.relationship),
                        recipient_name: recipient.name || '',
                     };

                     const invitationId = await sendTripInvitation(invitation);

                     if (invitationId) {
                        results.emailSuccess++;

                        // Also add to relationship_network collection for immediate display in People page
                        if (recipient.relationship?.type) {
                           const relationshipData = {
                              status: 'pending',
                              userId: currentUser.uid,
                              relationToUserId: null, // Will be filled later if user accepts invitation
                              relationToEmail: recipient.email,
                              relationType: recipient.relationship.type,
                              relationLabel: recipient.relationship.label || null,
                              notes: recipient.relationship.notes || null,
                              source: 'trip_invitation',
                              sourceId: selectedTripId,
                              createdAt: new Date(),
                           };

                           await addDoc(collection(db, 'relationship_network'), relationshipData);
                        }
                     }
                  } catch (err) {
                     console.log('🔍 Trip invitation error caught:', err);
                     console.log(
                        '🔍 Error message:',
                        err instanceof Error ? err.message : 'Unknown error'
                     );

                     // Check for specific error types
                     if (err instanceof Error) {
                        const errorMessage = err.message.toLowerCase();
                        console.log('🔍 Checking error message:', errorMessage);

                        if (
                           errorMessage.includes('already been invited') ||
                           errorMessage.includes('duplicate_invitation') ||
                           errorMessage.includes('already accepted') ||
                           errorMessage.includes('already_accepted') ||
                           errorMessage.includes('already a participant') ||
                           errorMessage.includes('already_participant') ||
                           errorMessage.includes('invitation has already been sent') ||
                           errorMessage.includes('user is already a participant')
                        ) {
                           console.log('🔍 Detected duplicate invitation');
                           results.emailDuplicate++;
                           results.duplicateEmails.push(recipient.email!);
                        } else {
                           console.log('🔍 Not a duplicate error, counting as failed');
                           results.emailFailed++;
                           results.failedEmails.push(recipient.email!);
                           console.error('Trip invitation error:', err);
                        }
                     } else {
                        console.log('🔍 Non-Error object, counting as failed');
                        results.emailFailed++;
                        results.failedEmails.push(recipient.email!);
                     }
                  }
               }
            }

            // Send event invitations if an event is selected
            if (selectedEventId && eventTitle) {
               for (const recipient of emailRecipients) {
                  try {
                     // Create invitation data
                     const invitation = {
                        eventId: selectedEventId,
                        eventTitle,
                        inviterId: currentUser.uid,
                        inviterName:
                           currentUser.displayName || currentUser.email || 'Event Organizer',
                        email: recipient.email!,
                        role: (recipient.role || 'editor') as EventRole,
                        customMessage: customMessage || undefined,
                        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
                        relationship: sanitizeRelationship(recipient.relationship),
                        recipient_name: recipient.name || '',
                     };

                     // Send invitation using the service

                     const invitationId = await sendEventInvitation(invitation);

                     if (invitationId) {
                        results.emailSuccess++;

                        // Also add to relationships collection for immediate display in People page
                        if (recipient.relationship?.type) {
                           const relationshipData = {
                              userId: currentUser.uid,
                              relationToUserId: null,
                              relationToEmail: recipient.email,
                              relationType: recipient.relationship.type,
                              relationLabel: recipient.relationship.label || null,
                              notes: recipient.relationship.notes || null,
                              source: 'event_invitation',
                              sourceId: selectedEventId,
                              createdAt: new Date(),
                           };

                           await addDoc(collection(db, 'relationships'), relationshipData);
                        }
                     }
                  } catch (err) {
                     console.log('🔍 Event invitation error caught:', err);
                     console.log(
                        '🔍 Error message:',
                        err instanceof Error ? err.message : 'Unknown error'
                     );

                     // Check for specific error types
                     if (err instanceof Error) {
                        const errorMessage = err.message.toLowerCase();
                        console.log('🔍 Checking error message:', errorMessage);

                        if (
                           errorMessage.includes('already been invited') ||
                           errorMessage.includes('duplicate_invitation') ||
                           errorMessage.includes('already accepted') ||
                           errorMessage.includes('already_accepted') ||
                           errorMessage.includes('already a participant') ||
                           errorMessage.includes('already_participant') ||
                           errorMessage.includes('invitation has already been sent') ||
                           errorMessage.includes('user is already a participant')
                        ) {
                           console.log('🔍 Detected duplicate invitation');
                           results.emailDuplicate++;
                           results.duplicateEmails.push(recipient.email!);
                        } else {
                           console.log('🔍 Not a duplicate error, counting as failed');
                           results.emailFailed++;
                           results.failedEmails.push(recipient.email!);
                           console.error('Event invitation error:', err);
                        }
                     } else {
                        console.log('🔍 Non-Error object, counting as failed');
                        results.emailFailed++;
                        results.failedEmails.push(recipient.email!);
                     }
                  }
               }
            }
         }

         let message = '';

         if (results.emailSuccess > 0) {
            // Email results
            if (results.emailSuccess > 0) {
               message += `${results.emailSuccess} email invitation${
                  results.emailSuccess !== 1 ? 's' : ''
               } sent. `;
            }

            if (results.emailDuplicate > 0) {
               // Get user names for duplicate emails
               const duplicateNames = results.duplicateEmails.map((email) => {
                  const recipient = emailRecipients.find((r) => r.email === email);
                  return recipient?.name || email;
               });

               message += `${duplicateNames.join(', ')} ${
                  results.emailDuplicate !== 1 ? 'are' : 'is'
               } already invited to this ${selectedTripId ? 'trip' : 'event'}. Use "Resend Invitation" in Manage Invitations page. `;
            }

            setSuccess(message.trim());

            // Reset the form if everything was successful
            if (results.emailFailed === 0) {
               resetForm();

               // Auto-close after 2 seconds if only successful, don't close if there were duplicates
               // since the user may want to see which were duplicates
               if (results.emailDuplicate === 0) {
                  onClose();
               }
            }
         }

         // Build detailed error messages
         let errorMessages = [];

         if (results.emailDuplicate > 0) {
            // Get user names for duplicate emails
            const duplicateNames = results.duplicateEmails.map((email) => {
               const recipient = emailRecipients.find((r) => r.email === email);
               return recipient?.name || email;
            });

            errorMessages.push(
               `${duplicateNames.join(', ')} ${results.emailDuplicate !== 1 ? 'are' : 'is'} already invited to this ${selectedTripId ? 'trip' : 'event'}. Please use the "Resend Invitation" option in the Manage Invitations page.`
            );
         }

         if (results.emailFailed > 0) {
            errorMessages.push(
               `Failed to send ${results.emailFailed} invitation${results.emailFailed !== 1 ? 's' : ''}: ${results.failedEmails.join(', ')}`
            );
         }

         // Handle phone invitations
         if (tabValue === 1) {
            console.log('🔍 Phone invitations: Starting phone invitation process');
            console.log('🔍 Phone recipients:', phoneRecipients);

            // For phone invitations
            if (selectedTripId && tripTitle) {
               console.log('🔍 Sending trip phone invitations for trip:', selectedTripId);
               for (const recipient of phoneRecipients) {
                  try {
                     const phoneInvitationData = {
                        tripId: selectedTripId,
                        phoneNumber: recipient.phoneNumber,
                        role: (recipient.role || 'editor') as 'viewer' | 'editor' | 'owner',
                        customMessage: customMessage || '',
                        recipientName: recipient.name || '',
                     };

                     console.log('🔍 Sending phone invitation with data:', phoneInvitationData);

                     const headers = await getAuthHeaders();
                     const response = await fetch('/api/trip/invite-phone', {
                        method: 'POST',
                        headers: {
                           ...headers,
                           'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(phoneInvitationData),
                     });

                     console.log(
                        '🔍 Phone invitation response:',
                        response.status,
                        response.statusText
                     );

                     if (!response.ok) {
                        const errorText = await response.text();
                        console.error('🔍 Phone invitation failed:', errorText);

                        // Check for duplicate invitation error
                        if (
                           errorText.includes('already been sent') ||
                           errorText.includes('invitation has already been sent')
                        ) {
                           throw new Error(
                              `An invitation has already been sent to this phone number. Please check your sent invitations or try a different number.`
                           );
                        }

                        // Check for Twilio regional permission error
                        if (
                           errorText.includes('21408') ||
                           errorText.includes('Permission to send an SMS has not been enabled')
                        ) {
                           throw new Error(
                              `SMS delivery not available for this region. Please contact support or try a different phone number.`
                           );
                        }

                        throw new Error(`Phone invitation failed: ${response.status} ${errorText}`);
                     }

                     const responseData = await response.json();
                     console.log('🔍 Phone invitation success:', responseData);

                     results.phoneSuccess++;
                  } catch (err) {
                     console.error('Error sending phone invitation:', err);
                     results.phoneFailed++;
                     results.failedEmails.push(
                        recipient.formattedNumber || recipient.phoneNumber || ''
                     );
                  }
               }
            }

            // Send event phone invitations if an event is selected
            if (selectedEventId && eventTitle) {
               console.log('🔍 Sending event phone invitations for event:', selectedEventId);
               for (const recipient of phoneRecipients) {
                  try {
                     const phoneInvitationData = {
                        eventId: selectedEventId,
                        phoneNumber: recipient.phoneNumber,
                        role:
                           (recipient.role || 'editor') === 'owner'
                              ? 'organizer'
                              : (recipient.role || 'editor') === 'editor'
                                ? 'attendee'
                                : 'attendee',
                        customMessage: customMessage || '',
                        recipientName: recipient.name || '',
                     };

                     console.log(
                        '🔍 Sending event phone invitation with data:',
                        phoneInvitationData
                     );

                     const headers = await getAuthHeaders();
                     const response = await fetch('/api/event/invite-phone', {
                        method: 'POST',
                        headers: {
                           ...headers,
                           'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(phoneInvitationData),
                     });

                     console.log(
                        '🔍 Event phone invitation response:',
                        response.status,
                        response.statusText
                     );

                     if (!response.ok) {
                        const errorText = await response.text();
                        console.error('🔍 Event phone invitation failed:', errorText);

                        // Check for duplicate invitation error
                        if (
                           errorText.includes('already been sent') ||
                           errorText.includes('invitation has already been sent')
                        ) {
                           throw new Error(
                              `An invitation has already been sent to this phone number. Please check your sent invitations or try a different number.`
                           );
                        }

                        // Check for Twilio regional permission error
                        if (
                           errorText.includes('21408') ||
                           errorText.includes('Permission to send an SMS has not been enabled')
                        ) {
                           throw new Error(
                              `SMS delivery not available for this region. Please contact support or try a different phone number.`
                           );
                        }

                        throw new Error(
                           `Event phone invitation failed: ${response.status} ${errorText}`
                        );
                     }

                     const responseData = await response.json();
                     console.log('🔍 Event phone invitation success:', responseData);

                     results.phoneSuccess++;
                  } catch (err) {
                     console.error('Error sending phone event invitation:', err);
                     results.phoneFailed++;
                     results.failedEmails.push(
                        recipient.formattedNumber || recipient.phoneNumber || ''
                     );
                  }
               }
            }

            // Show success message for phone invitations
            if (results.phoneSuccess > 0) {
               console.log('🔍 Phone invitations successful:', results.phoneSuccess);
               setSuccess(
                  `Successfully sent ${results.phoneSuccess} phone invitation${results.phoneSuccess !== 1 ? 's' : ''}!`
               );

               // Clear form
               setPhoneRecipients([]);
               setPhoneInput('');
               setCustomMessage('');
               resetRelationshipFields();

               // Trigger callback
               if (onInvitationSent) {
                  onInvitationSent();
               }

               // Auto-close after 2 seconds if only successful
               if (results.phoneFailed === 0) {
                  setTimeout(() => {
                     onClose();
                  }, 2000);
               }
            }

            // Show error message for phone invitations
            if (results.phoneFailed > 0) {
               console.log('🔍 Phone invitations failed:', results.phoneFailed);
               const phoneErrorMessage = `Failed to send ${results.phoneFailed} phone invitation${results.phoneFailed !== 1 ? 's' : ''}`;
               if (errorMessages.length > 0) {
                  errorMessages.push(phoneErrorMessage);
               } else {
                  setError(phoneErrorMessage);
               }
            }
         }

         if (errorMessages.length > 0) {
            setError(errorMessages.join('. '));
         }
      } catch (err) {
         setError('Failed to send invitations. Please try again.');
      } finally {
         setLoading(() => false);
      }
   };

   // const formatDate = (timestamp: Timestamp) => {
   //    if (!timestamp) return '';
   //    const date = timestamp.toDate();
   //    return date.toLocaleDateString();
   // };

   // const getRoleIcon = (role: string) => {
   //    switch (role) {
   //       case 'owner':
   //          return <PeopleIcon fontSize='small' />;
   //       case 'editor':
   //          return <EditIcon fontSize='small' />;
   //       default:
   //          return undefined;
   //    }
   // };

   // const getRoleDescription = (role: string) => {
   //    switch (role) {
   //       case 'owner':
   //          return 'Can edit details and manage participants';
   //       case 'editor':
   //          return 'Can edit details but cannot manage participants';
   //       default:
   //          return '';
   //    }
   // };

   const renderRelationshipLabel = (relType: RelationshipType, customLabel?: string) => {
      if (customLabel) return customLabel;

      return relationshipLabels[relType] || 'Connection';
   };

   // Add a sanitizeRelationship function near the top of the component
   const sanitizeRelationship = (relationship?: {
      type: RelationshipType;
      label?: string;
      notes?: string;
   }) => {
      if (!relationship) return null;

      return {
         type: relationship.type,
         label: relationship.label || null,
         notes: relationship.notes || null,
      };
   };

   return (
      <Dialog
         open={open}
         onClose={handleDialogClose}
         maxWidth='md'
         fullWidth
         fullScreen={false}
         sx={{
            '& .MuiDialog-paper': {
               margin: { xs: 1, sm: 2 },
               maxHeight: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' },
               width: { xs: 'calc(100% - 16px)', sm: 'auto' },
            },
         }}
      >
         <DialogTitle>
            <Typography variant='h6'>Send Invitations</Typography>
         </DialogTitle>

         <DialogContent dividers>
            {error && (
               <Alert
                  severity='error'
                  sx={{
                     mb: 2,
                     '& .MuiAlert-message': {
                        fontSize: '0.95rem',
                        lineHeight: 1.4,
                     },
                  }}
               >
                  {error}
               </Alert>
            )}

            {success && (
               <Alert severity='success' sx={{ mb: 2 }}>
                  {success}
               </Alert>
            )}

            <Box sx={{ mb: 3 }}>
               <Typography variant='subtitle1' color='primary'>
                  Invite people to events you've created in your personal account
               </Typography>
               <Typography variant='body2' color='text.secondary'>
                  Select the event you want to invite people to, then add recipients by
                  email or phone number.
               </Typography>
            </Box>

            <Grid container spacing={2}>
               {/* Trip selection - Hidden */}
               {/* <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                     <InputLabel id='trip-select-label'>Select Trip</InputLabel>
                     <Select
                        labelId='trip-select-label'
                        value={selectedTripId || ''}
                        onChange={(e) => setSelectedTripId(e.target.value)}
                        label='Select Trip'
                        disabled={tripsLoading || selectedEventId ? true : false}
                        sx={{ minWidth: 200 }}
                     >
                        <MenuItem value=''>
                           <em>None</em>
                        </MenuItem>
                        {tripsLoading ? (
                           <MenuItem disabled>
                              <CircularProgress size={20} sx={{ mr: 1 }} /> Loading trips...
                           </MenuItem>
                        ) : trips.length === 0 ? (
                           <MenuItem disabled>No trips found</MenuItem>
                        ) : (
                           trips.map((trip) => (
                              <MenuItem key={trip.id} value={trip.id}>
                                 {trip.title}
                              </MenuItem>
                           ))
                        )}
                     </Select>
                     <FormHelperText>
                        {trips.length > 0
                           ? 'Select one of your personal trips to invite people to'
                           : 'No trips available - create a trip first'}
                     </FormHelperText>
                  </FormControl>
               </Grid> */}

               {/* Event selection */}
               <Grid item xs={12}>
                  <FormControl fullWidth>
                     <InputLabel id='event-select-label'>Select Event</InputLabel>
                     <Select
                        labelId='event-select-label'
                        value={selectedEventId || ''}
                        onChange={(e) => setSelectedEventId(e.target.value)}
                        label='Select Event'
                        disabled={eventsLoading}
                        sx={{ minWidth: 200 }}
                     >
                        <MenuItem value=''>
                           <em>None</em>
                        </MenuItem>
                        {eventsLoading ? (
                           <MenuItem disabled>
                              <CircularProgress size={20} sx={{ mr: 1 }} /> Loading events...
                           </MenuItem>
                        ) : events.length === 0 ? (
                           <MenuItem disabled>No events found</MenuItem>
                        ) : (
                           events.map((event) => (
                              <MenuItem key={event.id} value={event.id}>
                                 {event.title}
                              </MenuItem>
                           ))
                        )}
                     </Select>
                     <FormHelperText>
                        {events.length > 0
                           ? 'Select one of your personal events to invite people to'
                           : 'No events available - create an event first'}
                     </FormHelperText>
                  </FormControl>
               </Grid>

               {/* Tabs for Email vs SMS */}
               <Grid item xs={12}>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                     <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        aria-label='invitation-type-tabs'
                     >
                        <Tab
                           icon={<EmailIcon />}
                           iconPosition='start'
                           label='Email'
                           id='invitation-tab-0'
                           aria-controls='invitation-tabpanel-0'
                        />
                        <Tab
                           icon={<PhoneIcon />}
                           iconPosition='start'
                           label='Phone'
                           id='invitation-tab-1'
                           aria-controls='invitation-tabpanel-1'
                        />
                     </Tabs>
                  </Box>

                  {/* Email Tab Panel */}
                  <div
                     role='tabpanel'
                     hidden={tabValue !== 0}
                     id='invitation-tabpanel-0'
                     aria-labelledby='invitation-tab-0'
                  >
                     <Box sx={{ mt: 2 }}>
                        <Typography variant='subtitle1' gutterBottom>
                           Add Recipients by Email
                        </Typography>

                        {/* Contact Selection Toggle */}
                        {contacts.length > 0 && (
                           <Box sx={{ mb: 2 }}>
                              <Typography variant='subtitle2' sx={{ mb: 1 }}>
                                 Choose invitation method:
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                 <Button
                                    variant={!useExistingContact ? 'contained' : 'outlined'}
                                    size='small'
                                    onClick={() => setUseExistingContact(false)}
                                    startIcon={<EmailIcon />}
                                 >
                                    New Email
                                 </Button>
                                 <Button
                                    variant={useExistingContact ? 'contained' : 'outlined'}
                                    size='small'
                                    onClick={() => setUseExistingContact(true)}
                                    startIcon={<ContactMailIcon />}
                                 >
                                    Previous Contact ({contacts.length})
                                 </Button>
                              </Box>
                           </Box>
                        )}

                        <TextField
                           value={recipientsName}
                           onChange={(e) => setRecipientsName(e.target.value)}
                           fullWidth
                           label='Recipients Name'
                        />
                     </Box>

                     {tabValue === 0 && (
                        <Box sx={{ pt: 2 }}>
                           <Typography variant='subtitle1' gutterBottom>
                              Email Address
                           </Typography>

                           <Grid container spacing={2}>
                              <Grid item xs={12}>
                                 <Box sx={{ display: 'flex', gap: 1 }}>
                                    {/* Contact Selection or Email Input */}
                                    {useExistingContact && contacts.length > 0 ? (
                                       <Autocomplete
                                          options={contacts}
                                          value={selectedContact}
                                          onChange={(event, newValue) => {
                                             setSelectedContact(newValue || null);
                                             // Auto-fill recipient name when contact is selected
                                             if (newValue) {
                                                setRecipientsName(newValue.name);
                                             }
                                          }}
                                          getOptionLabel={(option) => option.name}
                                          renderOption={(props, option) => (
                                             <Box component='li' {...props}>
                                                <Box>
                                                   <Typography
                                                      variant='body2'
                                                      sx={{ fontWeight: 500 }}
                                                   >
                                                      {option.name}
                                                   </Typography>
                                                   <Typography
                                                      variant='caption'
                                                      color='text.secondary'
                                                   >
                                                      {option.email}
                                                   </Typography>
                                                </Box>
                                             </Box>
                                          )}
                                          renderInput={(params) => (
                                             <TextField
                                                {...params}
                                                label='Select Previous Contact'
                                                disabled={loading}
                                                InputProps={{
                                                   ...params.InputProps,
                                                   startAdornment: (
                                                      <ContactMailIcon
                                                         sx={{ mr: 1, color: 'text.secondary' }}
                                                      />
                                                   ),
                                                }}
                                             />
                                          )}
                                          loading={loadingContacts}
                                          loadingText='Loading contacts...'
                                          noOptionsText='No previous contacts found'
                                          fullWidth
                                          sx={{ flex: 1 }}
                                       />
                                    ) : (
                                       <TextField
                                          fullWidth
                                          label='Email Address'
                                          value={emailInput}
                                          onChange={(e) => setEmailInput(e.target.value)}
                                          onKeyPress={(e) => {
                                             if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleAddEmail();
                                             }
                                          }}
                                          disabled={loading}
                                          placeholder='Enter email address and press Enter'
                                       />
                                    )}
                                    <Button
                                       variant='contained'
                                       onClick={handleAddEmail}
                                       disabled={
                                          loading ||
                                          (!useExistingContact && !emailInput) ||
                                          (useExistingContact && !selectedContact)
                                       }
                                       startIcon={<PersonAddIcon />}
                                    >
                                       Add
                                    </Button>
                                 </Box>
                              </Grid>

                              {/* Relationship fields */}
                              <Grid item xs={12}>
                                 <Accordion
                                    expanded={showRelationshipFields}
                                    onChange={() =>
                                       setShowRelationshipFields(!showRelationshipFields)
                                    }
                                    sx={{ mb: 2 }}
                                 >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                       <Typography>
                                          <PeopleIcon
                                             sx={{
                                                mr: 1,
                                                verticalAlign: 'middle',
                                                fontSize: '1.1rem',
                                             }}
                                          />
                                          Add relationship details
                                       </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                       <Grid container spacing={2}>
                                          <Grid item xs={12}>
                                             <FormControl fullWidth size='small'>
                                                <InputLabel id='relationship-type-label'>
                                                   Relationship Type
                                                </InputLabel>
                                                <Select
                                                   labelId='relationship-type-label'
                                                   value={selectedRelationshipType}
                                                   onChange={handleRelationshipTypeChange}
                                                   label='Relationship Type'
                                                >
                                                   <MenuItem value=''>
                                                      <em>None</em>
                                                   </MenuItem>

                                                   {Object.entries(relationshipCategories).map(
                                                      ([category, types]) => [
                                                         <ListSubheader key={category}>
                                                            {category}
                                                         </ListSubheader>,
                                                         ...types.map((type) => (
                                                            <MenuItem key={type} value={type}>
                                                               {
                                                                  relationshipLabels[
                                                                     type as RelationshipType
                                                                  ]
                                                               }
                                                            </MenuItem>
                                                         )),
                                                      ]
                                                   )}
                                                </Select>
                                                <FormHelperText>
                                                   Relationship information helps organize your
                                                   network
                                                </FormHelperText>
                                             </FormControl>
                                          </Grid>

                                          {selectedRelationshipType && (
                                             <Grid item xs={12}>
                                                <TextField
                                                   fullWidth
                                                   size='small'
                                                   label='Custom Relationship Label (Optional)'
                                                   placeholder='e.g. College Roommate, Team Lead'
                                                   value={customRelationshipLabel}
                                                   onChange={(e) =>
                                                      setCustomRelationshipLabel(e.target.value)
                                                   }
                                                   helperText='Add a more specific description of your relationship'
                                                />
                                             </Grid>
                                          )}
                                       </Grid>
                                    </AccordionDetails>
                                 </Accordion>
                              </Grid>

                              {/* Permission Level Selection - positioned after email input */}
                              <Grid item xs={12}>
                                 <Typography variant='subtitle1' gutterBottom>
                                    Permission Level
                                 </Typography>
                                 <FormControl fullWidth size='small'>
                                    <InputLabel id='role-selection-label'>Role</InputLabel>
                                    <Select
                                       labelId='role-selection-label'
                                       value={selectedRole}
                                       onChange={(e) =>
                                          setSelectedRole(
                                             e.target.value as 'viewer' | 'editor' | 'owner'
                                          )
                                       }
                                       label='Role'
                                    >
                                       <MenuItem value='viewer'>
                                          <Box
                                             sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                          >
                                             <PersonIcon fontSize='small' />
                                             Viewer - Can view trip, event, and album content only
                                          </Box>
                                       </MenuItem>
                                       <MenuItem value='editor'>
                                          <Box
                                             sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                          >
                                             <EditIcon fontSize='small' />
                                             Editor - Can view and edit trip, event, and album
                                             content
                                          </Box>
                                       </MenuItem>
                                       <MenuItem value='owner'>
                                          <Box
                                             sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                          >
                                             <CoOwnerIcon fontSize='small' />
                                             Owner - Full access
                                          </Box>
                                       </MenuItem>
                                    </Select>
                                 </FormControl>
                              </Grid>

                              <Grid item xs={12}>
                                 <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {emailRecipients.map((recipient, index) => (
                                       <Chip
                                          key={index}
                                          label={recipient.email || ''}
                                          onDelete={() =>
                                             recipient.email && handleRemoveEmail(recipient.email)
                                          }
                                          color='primary'
                                          variant='outlined'
                                       />
                                    ))}
                                    {emailRecipients.map(
                                       (recipient, index) =>
                                          recipient.relationship &&
                                          recipient.relationship.type && (
                                             <Chip
                                                key={`rel-${index}`}
                                                label={renderRelationshipLabel(
                                                   recipient.relationship.type,
                                                   recipient.relationship.label
                                                )}
                                                color='info'
                                                icon={<PeopleIcon fontSize='small' />}
                                             />
                                          )
                                    )}
                                 </Box>
                              </Grid>
                           </Grid>
                        </Box>
                     )}
                  </div>

                  {/* Phone Tab Panel */}
                  <div
                     role='tabpanel'
                     hidden={tabValue !== 1}
                     id='invitation-tabpanel-1'
                     aria-labelledby='invitation-tab-1'
                  >
                     {tabValue === 1 && (
                        <Box sx={{ pt: 2 }}>
                           <Typography variant='subtitle1' gutterBottom>
                              Add Recipients by Phone Number
                           </Typography>

                           <Grid container spacing={2}>
                              {/* Recipient Name - Mandatory Field */}
                              <Grid item xs={12}>
                                 <TextField
                                    fullWidth
                                    label='Recipient Name *'
                                    value={recipientsName}
                                    onChange={(e) => setRecipientsName(e.target.value)}
                                    placeholder='Enter recipient name'
                                    required
                                    error={!recipientsName.trim()}
                                    helperText={
                                       !recipientsName.trim()
                                          ? 'Name is required for SMS invitations'
                                          : ''
                                    }
                                    sx={{
                                       '& .MuiOutlinedInput-root': {
                                          fontSize: { xs: '16px', sm: '14px' }, // Prevent zoom on iOS
                                       },
                                    }}
                                 />
                              </Grid>

                              <Grid item xs={12}>
                                 <Box
                                    sx={{
                                       display: 'flex',
                                       gap: { xs: 1, sm: 2 },
                                       flexDirection: { xs: 'column', sm: 'row' },
                                       alignItems: { xs: 'stretch', sm: 'flex-start' },
                                    }}
                                 >
                                    <Autocomplete
                                       options={COUNTRIES}
                                       getOptionLabel={(option) =>
                                          `${option.flag} ${option.name} (${option.dialCode})`
                                       }
                                       value={selectedCountry}
                                       onChange={(event, newValue) => {
                                          if (newValue) {
                                             setSelectedCountry(newValue);
                                          }
                                       }}
                                       renderInput={(params) => (
                                          <TextField
                                             {...params}
                                             label='Country'
                                             variant='outlined'
                                             sx={{
                                                '& .MuiOutlinedInput-root': {
                                                   fontSize: { xs: '16px', sm: '14px' },
                                                },
                                             }}
                                          />
                                       )}
                                       renderOption={(props, option) => (
                                          <Box component='li' {...props}>
                                             <span style={{ marginRight: 8 }}>{option.flag}</span>
                                             {option.name} ({option.dialCode})
                                          </Box>
                                       )}
                                       sx={{
                                          width: { xs: '100%', sm: 200 },
                                          minWidth: { xs: 'auto', sm: 200 },
                                       }}
                                       disabled={loading}
                                    />
                                    <TextField
                                       fullWidth
                                       label='Phone Number'
                                       value={phoneInput}
                                       onChange={(e) => setPhoneInput(e.target.value)}
                                       onKeyPress={(e) => {
                                          if (e.key === 'Enter') {
                                             e.preventDefault();
                                             handleAddPhone();
                                          }
                                       }}
                                       disabled={loading}
                                       placeholder='Enter phone number'
                                       InputProps={{
                                          startAdornment: (
                                             <Box
                                                sx={{
                                                   display: 'flex',
                                                   alignItems: 'center',
                                                   mr: 1,
                                                   color: 'text.secondary',
                                                   fontSize: '0.875rem',
                                                   fontWeight: 500,
                                                }}
                                             >
                                                {selectedCountry.flag} {selectedCountry.dialCode}
                                             </Box>
                                          ),
                                       }}
                                       sx={{
                                          '& .MuiOutlinedInput-root': {
                                             fontSize: { xs: '16px', sm: '14px' }, // Prevent zoom on iOS
                                          },
                                       }}
                                    />
                                    <Button
                                       variant='contained'
                                       onClick={handleAddPhone}
                                       disabled={loading || !phoneInput || !recipientsName.trim()}
                                       startIcon={<PersonAddIcon />}
                                       sx={{
                                          minWidth: { xs: '100%', sm: 120 },
                                          height: { xs: 56, sm: 'auto' }, // Match TextField height on mobile
                                          fontSize: { xs: '16px', sm: '14px' },
                                          py: { xs: 2, sm: 1 },
                                       }}
                                    >
                                       Add
                                    </Button>
                                 </Box>
                              </Grid>

                              <Grid item xs={12}>
                                 <Accordion
                                    expanded={showRelationshipFields}
                                    onChange={() =>
                                       setShowRelationshipFields(!showRelationshipFields)
                                    }
                                    sx={{ mb: 2 }}
                                 >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                       <Typography>
                                          <PeopleIcon
                                             sx={{
                                                mr: 1,
                                                verticalAlign: 'middle',
                                                fontSize: '1.1rem',
                                             }}
                                          />
                                          Add relationship details
                                       </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                       <Grid container spacing={2}>
                                          <Grid item xs={12}>
                                             <FormControl fullWidth size='small'>
                                                <InputLabel id='phone-relationship-type-label'>
                                                   Relationship Type
                                                </InputLabel>
                                                <Select
                                                   labelId='phone-relationship-type-label'
                                                   value={selectedRelationshipType}
                                                   onChange={handleRelationshipTypeChange}
                                                   label='Relationship Type'
                                                >
                                                   <MenuItem value=''>
                                                      <em>None</em>
                                                   </MenuItem>

                                                   {Object.entries(relationshipCategories).map(
                                                      ([category, types]) => [
                                                         <ListSubheader key={category}>
                                                            {category}
                                                         </ListSubheader>,
                                                         ...types.map((type) => (
                                                            <MenuItem key={type} value={type}>
                                                               {
                                                                  relationshipLabels[
                                                                     type as RelationshipType
                                                                  ]
                                                               }
                                                            </MenuItem>
                                                         )),
                                                      ]
                                                   )}
                                                </Select>
                                                <FormHelperText>
                                                   Relationship information helps organize your
                                                   network
                                                </FormHelperText>
                                             </FormControl>
                                          </Grid>

                                          {selectedRelationshipType && (
                                             <Grid item xs={12}>
                                                <TextField
                                                   fullWidth
                                                   size='small'
                                                   label='Custom Relationship Label (Optional)'
                                                   placeholder='e.g. College Roommate, Team Lead'
                                                   value={customRelationshipLabel}
                                                   onChange={(e) =>
                                                      setCustomRelationshipLabel(e.target.value)
                                                   }
                                                   helperText='Add a more specific description of your relationship'
                                                />
                                             </Grid>
                                          )}
                                       </Grid>
                                    </AccordionDetails>
                                 </Accordion>
                              </Grid>

                              <Grid item xs={12}>
                                 <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {phoneRecipients.map((recipient, index) => (
                                       <Chip
                                          key={index}
                                          label={`${recipient.name || 'Unknown'} - ${recipient.formattedNumber || ''}`}
                                          onDelete={() =>
                                             recipient.formattedNumber &&
                                             handleRemovePhone(recipient.formattedNumber)
                                          }
                                          color='secondary'
                                          variant='outlined'
                                       />
                                    ))}
                                    {phoneRecipients.map(
                                       (recipient, index) =>
                                          recipient.relationship &&
                                          recipient.relationship.type && (
                                             <Chip
                                                key={`rel-${index}`}
                                                label={renderRelationshipLabel(
                                                   recipient.relationship.type,
                                                   recipient.relationship.label
                                                )}
                                                color='info'
                                                icon={<PeopleIcon fontSize='small' />}
                                             />
                                          )
                                    )}
                                 </Box>
                              </Grid>
                           </Grid>
                        </Box>
                     )}
                  </div>
               </Grid>

               {/* Custom Message */}
               <Grid item xs={12}>
                  <Typography variant='subtitle1' gutterBottom>
                     Custom Message (Optional)
                  </Typography>
                  <TextField
                     fullWidth
                     multiline
                     rows={3}
                     label='Add a personal message'
                     value={customMessage}
                     onChange={(e) => setCustomMessage(e.target.value)}
                     disabled={loading}
                     placeholder='Enter a personal message to include with the invitation'
                  />
               </Grid>
            </Grid>

            {/* Permission warning */}
            {(selectedTripId || selectedEventId) && !canSendInvites && (
               <Alert severity='warning' sx={{ mx: 3, mb: 2 }}>
                  You don't have permission to send invitations for this{' '}
                  {selectedTripId ? 'trip' : 'event'}. Only owners and editors can invite new
                  members.
               </Alert>
            )}
         </DialogContent>

         <DialogActions
            sx={{
               flexDirection: { xs: 'column', sm: 'row' },
               gap: { xs: 1, sm: 0 },
               p: { xs: 2, sm: 1 },
            }}
         >
            <Button
               onClick={handleDialogClose}
               disabled={loading}
               sx={{
                  order: { xs: 2, sm: 1 },
                  minWidth: { xs: '100%', sm: 'auto' },
                  fontSize: { xs: '16px', sm: '14px' },
               }}
            >
               Cancel
            </Button>
            <Button
               key='send-invitations-button'
               variant='contained'
               color='primary'
               onClick={handleSendInvitations}
               disabled={
                  loading ||
                  (!selectedTripId && !selectedEventId) ||
                  (tabValue === 0 && emailRecipients.length === 0) ||
                  (tabValue === 1 && phoneRecipients.length === 0) ||
                  !canSendInvites
               }
               startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
               sx={{
                  order: { xs: 1, sm: 2 },
                  minWidth: { xs: '100%', sm: 'auto' },
                  fontSize: { xs: '16px', sm: '14px' },
                  py: { xs: 1.5, sm: 1 },
               }}
            >
               {loading ? 'Sending...' : 'Send Invitations'}
            </Button>
         </DialogActions>
      </Dialog>
   );
};

export default SendInvitationDialog;
